// API клиент для работы с FastAPI бэкендом

import axios from 'axios';
import {
  Account,
  ModelInfo,
  DashboardSummary,
  ChartData,
  TopModelData,
  CampaignMetrics,
  ExportData,
  LoginResponse,
  BotAccount,
  NightModeSettings,
  BotBalanceThresholds,
  BotPauseResult,
  AddBotAccountRequest,
  BotItem,
} from '../types/api';
import type {
  ContractTemplateDetail,
  ContractTemplateSummary,
  ContractGenerationDetail,
  ContractGenerationSummary,
  GenerateContractRequest,
  GenerateContractResponse,
  UpdateContractTemplateRequest,
} from '../types/contracts';
import { clearStoredAuthSession, shouldForceLogoutOn401 } from '../utils/authSession';

  // Используем простой строковый fallback
const API_BASE_URL = '';
const OPTIONAL_404_ENDPOINTS = new Set([
  '/api/v1/parser/cookie-pool-status',
]);

// Types for Client Keys
export interface ClientKey {
  id: number;
  key: string;
  name: string;
  description: string | null;
  allowed_accounts: string[];
  allowed_account_ids?: string[];
  assigned_pm_user_id?: number | null;
  created_at: string;
  is_active: boolean;
  active_chats_count: number;
  summaries_enabled?: boolean;
  positions_enabled?: boolean;
  missed_enabled?: boolean;
  parser_enabled?: boolean;
  balance_enabled?: boolean;
  parser_runs_per_day?: number;
}

export interface ClientKeyDetail extends ClientKey {
  telegram_chats: number[];
  created_by_user_id: number;
}

export interface CreateClientKeyRequest {
  name: string;
  description?: string;
  allowed_accounts: string[];
  assigned_pm_user_id?: number | null;
  summaries_enabled?: boolean;
  positions_enabled?: boolean;
  missed_enabled?: boolean;
  parser_enabled?: boolean;
  balance_enabled?: boolean;
  parser_runs_per_day?: number;
}

export interface UpdateClientKeyRequest {
  name?: string;
  description?: string;
  allowed_accounts?: string[];
  summaries_enabled?: boolean | null;
  positions_enabled?: boolean | null;
  missed_enabled?: boolean | null;
  parser_enabled?: boolean | null;
  balance_enabled?: boolean | null;
  parser_runs_per_day?: number | null;
  is_active?: boolean;
  assigned_pm_user_id?: number | null;
}

export interface ClientChat {
  chat_id: number;
  chat_title: string;
  chat_type: string;
  activated_at: string;
  last_activity: string;
  is_active: boolean;
}

export interface ClientKeysStats {
  total_keys: number;
  active_keys: number;
  inactive_keys: number;
  total_chats: number;
  active_chats: number;
}

export interface ServerProxyItem {
  id: number;
  name: string;
  proxy_type: 'http' | 'https' | 'socks5';
  host: string;
  port: number;
  login: string | null;
  provider_proxy_id: number | null;
  has_password: boolean;
  is_active: boolean;
  priority: number;
  last_check_status: string | null;
  last_check_ip: string | null;
  last_check_error: string | null;
  last_check_at: string | null;
  last_equipment_change_at: string | null;
  last_equipment_eid: number | null;
  last_equipment_geo: string | null;
  last_external_ip: string | null;
  provider_proxy_exp: string | null;
  provider_proxy_operator: string | null;
  provider_proxy_auto_renewal: number | null;
  provider_proxy_reboot_time: number | null;
  provider_proxy_auto_change_equipment: number | null;
  provider_proxy_comment: string | null;
  provider_geoid: number | null;
  provider_id_country: number | null;
  provider_id_city: number | null;
  is_provider_server_proxy?: boolean | null;
  last_provider_sync_at: string | null;
  equipment_fail_count: number;
  fail_count: number;
  monitoring_only?: boolean;
  client_only?: boolean;
  created_at: string | null;
  updated_at: string | null;
}

export interface ServerProxyCreateRequest {
  name: string;
  proxy_type: 'http' | 'https' | 'socks5';
  host: string;
  port: number;
  login?: string | null;
  password?: string | null;
  provider_proxy_id?: number | null;
}

export interface ServerProxyBulkCreateResult {
  created: ServerProxyItem[];
  skipped: Array<{ index: number; host?: string; port?: number; reason: string }>;
  created_count: number;
  skipped_count: number;
}

export interface ServerProxyUpdateRequest {
  name?: string | null;
  proxy_type?: 'http' | 'https' | 'socks5' | null;
  host?: string | null;
  port?: number | null;
  login?: string | null;
  password?: string | null;
  provider_proxy_id?: number | null;
  is_active?: boolean | null;
  priority?: number | null;
  monitoring_only?: boolean | null;
  client_only?: boolean | null;
}

export interface ServerProxyProviderStatus {
  space_configured: boolean;
  balance: number | string | null;
  provider_proxy_count: number | null;
  provider_server_proxy_count?: number | null;
  provider_skipped_proxy_count?: number | null;
  last_sync_at: string | null;
  error: string | null;
}

export interface ServerProxyProviderSyncResult {
  ok: boolean;
  created_count: number;
  updated_count: number;
  provider_count: number;
  provider_server_count?: number;
  skipped_non_server_count?: number;
  created: ServerProxyItem[];
  updated: ServerProxyItem[];
}

export interface ServerProxyProviderPurchaseRequest {
  id_country: number;
  period: 1 | 7 | 30 | 90 | 365;
  num: number;
  operator?: string | null;
  geoid?: number | null;
  id_city?: number | null;
  coupons_code?: string | null;
  auto_renewal: 0 | 1;
}

export interface ServerProxyProviderChangeEquipmentRequest {
  operator?: string | null;
  geoid?: number | null;
  id_country?: number | null;
  id_city?: number | null;
  eid?: number | null;
  add_to_black_list: boolean;
  check_spam: boolean;
}

export interface ServerProxyProviderSettingsRequest {
  proxy_reboot_time?: number | null;
  proxy_auto_renewal?: 0 | 1 | null;
  proxy_auto_change_equipment?: 0 | 1 | 2 | 3 | 4 | null;
  proxy_comment?: string | null;
}

export interface DolphinProxyLastCheck {
  created_at: string | null;
  status: boolean | null;
  ip: string | null;
  country: string | null;
  city: string | null;
  found: number | null;
  total: number | null;
}

export interface DolphinProxySummary {
  proxies_total: number;
  profiles_total: number;
  profiles_without_proxy: number;
  failed_proxies: number;
  proxies_without_check: number;
  shared_proxies: number;
  unused_proxies: number;
  missing_proxy_profiles: number;
  last_sync_at: string | null;
  last_sync_error: string | null;
}

export interface DolphinProxyItem {
  id: number;
  name: string | null;
  type: 'http' | 'socks4' | 'socks5' | 'ssh' | string;
  host: string;
  port: number | null;
  login: string | null;
  has_password: boolean;
  change_ip_url_configured: boolean;
  provider: string | null;
  city_history: string[];
  browser_profiles_count: number;
  last_check: DolphinProxyLastCheck | null;
  admin_note: string | null;
  synced_at: string | null;
}

export interface DolphinProfileItem {
  id: number;
  name: string;
  status_name: string | null;
  status_color: string | null;
  platform: string | null;
  browser_type: string | null;
  main_website: string | null;
  proxy_id: number | null;
  proxy_name: string | null;
  proxy_host: string | null;
  proxy_type: string | null;
  folder: number | null;
  tags: string[];
  admin_note: string | null;
  locked: boolean;
  synced_at: string | null;
}

export interface DolphinOperationItem {
  id: number;
  action: string;
  actor_user_id: number | null;
  actor_username: string | null;
  target_type: string;
  target_id: number | null;
  status: string;
  message: string | null;
  payload: Record<string, any>;
  created_at: string;
}

export interface DolphinBatchPlanRow {
  profile_id: number;
  profile_name: string;
  city: string | null;
  issue: 'missing' | 'shared' | 'failed';
  current_proxy_id: number | null;
  current_proxy_label: string | null;
  recommended_proxy_id: number | null;
  recommended_proxy_label: string | null;
  reason: string;
  status: 'ready' | 'blocked';
}

export interface DolphinProxyRecommendation {
  profile_id: number;
  profile_name: string;
  city: string | null;
  issue: 'missing' | 'shared' | 'failed' | 'unchecked' | 'ok';
  current_proxy_id: number | null;
  current_proxy_label: string | null;
  recommended_proxy_id: number | null;
  recommended_proxy_label: string | null;
  reason: string;
  status: 'ready' | 'blocked';
}

export interface DolphinSyncResult {
  success: boolean;
  proxies_synced: number;
  profiles_synced: number;
  last_sync_at: string;
  errors: string[];
}

export interface DolphinProxyCheckResultItem {
  proxy_id: number;
  success: boolean;
  message?: string | null;
  proxy?: DolphinProxyItem | null;
}

export interface DolphinProxyCheckBulkResult {
  success: boolean;
  changed: number;
  failed: number;
  results: DolphinProxyCheckResultItem[];
}

export interface RegistryAccountPasswordStatus {
  account_name: string;
  has_password: boolean;
  updated_at: string | null;
}

export interface RegistryAccountPasswordReveal extends RegistryAccountPasswordStatus {
  password: string | null;
}

export interface DolphinProxyCreateRequest {
  type: 'http' | 'socks4' | 'socks5' | 'ssh';
  host: string;
  port: number;
  login?: string;
  password?: string;
  name?: string;
  changeIpUrl?: string;
}

export interface DolphinProxyUpdateRequest extends Partial<DolphinProxyCreateRequest> {
  admin_note?: string;
}

// Создаем axios instance с настройками
export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Добавляем перехватчик для обработки ошибок
api.interceptors.request.use(
  (config) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers: any = config.headers;
      if (headers) {
        if (typeof headers.delete === 'function') {
          headers.delete('Content-Type');
          headers.delete('content-type');
        } else {
          delete headers['Content-Type'];
          delete headers['content-type'];
        }
      }
    }
    console.log('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.method?.toUpperCase(), response.config.url);
    return response;
  },
  async (error) => {
    const maybeBlob = error?.response?.data;
    if (typeof Blob !== 'undefined' && maybeBlob instanceof Blob && maybeBlob.type?.includes('application/json')) {
      try {
        const text = await maybeBlob.text();
        const parsed = JSON.parse(text);
        error.response.data = parsed;
      } catch {
      }
    }
    const requestUrl = error.config?.url;
    const isOptional404 =
      error.response?.status === 404 &&
      typeof requestUrl === 'string' &&
      OPTIONAL_404_ENDPOINTS.has(requestUrl);

    if (!isOptional404) {
      console.error('API Error:', error.config?.method?.toUpperCase(), requestUrl);
    }
    const dataPreview = (() => {
      const data = error.response?.data;
      if (!data) return data;
      if (typeof data === 'string') return data;
      if (typeof data === 'object') {
        try {
          return JSON.stringify(data);
        } catch {
          return '[unserializable object]';
        }
      }
      return String(data);
    })();
    if (!isOptional404) {
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        dataPreview,
        message: error.message,
        url: `${API_BASE_URL}${requestUrl}`
      });
    }

    const headers = error?.response?.headers || {};
    const wwwAuthenticate =
      headers['www-authenticate'] ||
      headers['WWW-Authenticate'] ||
      null;

    // Выходим только при реальной потере авторизации, а не на любом 401
    if (shouldForceLogoutOn401({
      status: error.response?.status,
      url: error.config?.url,
      wwwAuthenticate,
      detail: error.response?.data,
    })) {
      clearStoredAuthSession();
      setAuthToken(null);
      if (window.location.pathname !== '/') {
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// Функция для установки токена авторизации
export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
  }
};

const parseFilenameFromDisposition = (contentDisposition?: string, contentType?: string): string => {
  if (!contentDisposition) {
    return contentType?.includes('application/pdf')
      ? 'contract-filled.pdf'
      : 'contract-filled.docx';
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return utf8Match[1];
    }
  }

  const fallbackMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  if (fallbackMatch?.[1]) {
    return fallbackMatch[1];
  }

  return contentType?.includes('application/pdf')
    ? 'contract-filled.pdf'
    : 'contract-filled.docx';
};

export type ParserSortConfig = {
  key: string;
  direction: 'asc' | 'desc';
} | null;

export type ParserDiffFilters = {
  neg: boolean;
  zero: boolean;
  pos: boolean;
  none: boolean;
};

export type ParserCategoryHighlightFilters = {
  green: boolean;
  yellow: boolean;
  red: boolean;
  none: boolean;
};

export type ParserModePreferences = {
  show_filters: boolean;
  show_columns: boolean;
  hidden_column_keys: string[];
  screenshot_hidden_column_keys: string[];
  sort_config: ParserSortConfig;
  diff_base: 'cheapest_new' | 'overall_min_new' | 'overall_avg' | 'avg_top10';
  category_price_base: 'cheapest_new' | 'overall_min_new' | 'overall_avg' | 'avg_top10';
  category_price_bases: Record<string, 'cheapest_new' | 'overall_min_new' | 'overall_avg' | 'avg_top10'>;
  diff_filters: ParserDiffFilters;
  category_highlight_filters: ParserCategoryHighlightFilters;
  seller_mode_enabled: boolean;
  seller_mode_names: string[];
  seller_mode_name?: string;  // backward compat
  seller_filter_enabled?: boolean;
  seller_name?: string;        // backward compat
  exclude_competitor_enabled: boolean;
  exclude_competitor_names: string[];
};

export type ParserPreferences = {
  version: number;
  active_mode: 'prices' | 'positions' | 'combined' | 'periodic' | 'profile_metrics' | 'competitor_prices';
  speed: 'fast' | 'normal';
  full_fetch: boolean;
  city_filter_enabled: boolean;
  link_format_2: boolean;
  used_only: boolean;
  only_promoted_ids: boolean;
  dev_mode: boolean;
  server_proxy_ids: number[];
  modes: {
    prices: ParserModePreferences;
    positions: ParserModePreferences;
    combined: ParserModePreferences;
    periodic: ParserModePreferences;
    profile_metrics: ParserModePreferences;
    competitor_prices: ParserModePreferences;
  };
  updated_at: string | null;
};

export type ParserCaptchaAccess = {
  method: 'rdp' | 'web';
  web_url: string | null;
  warning: string | null;
  rdp: {
    host: string;
    configured_host?: string | null;
    port: number;
    username: string;
    domain: string | null;
  };
};

export type CookieEvent = {
  ts: string;
  event_type: 'purchased' | 'purchase_failed' | 'skipped_stuck_ip';
  reason: 'session_expired' | 'attempt_threshold' | 'stuck_ip';
  recovery_attempt: number;
  ip_before: string | null;
  ip_after: string | null;
  ip_changed: boolean;
  unblock_status: string;
  success: boolean;
  log_lines: string[];
};

export type ScheduledJobResponse = {
  job_id: string;
  scheduled_at: number;
  scheduled_at_msk: string;
  time_until_msk: string;
  worksheet: string;
  mode: string;
  speed: string;
  full_fetch: boolean;
  status: 'pending' | 'queued' | 'running' | 'done' | 'cancelled' | 'error';
  run_id: string | null;
  error: string | null;
  created_at: number;
  repeat_daily: boolean;
  repeat_weekdays?: number[] | null;
  no_city_filter: boolean;
  link_format_2: boolean;
  used_only: boolean;
  only_promoted_ids: boolean;
  positions_screenshot_max_pos: number;
  periodic_end_time?: string | null;
  periodic_end_at_msk?: string | null;
  periodic_interval: number | null;
  profile_url?: string | null;
  iphone_only?: boolean;
  city_merge_mode?: boolean;
  city_merge_city?: string | null;
  city_merge_worksheets?: string[] | null;
  city_merge_market_mode?: 'account_competitors' | 'city_group_owned';
  city_template_mode?: boolean;
  city_template_city?: string | null;
  excluded_worksheet?: string | null;
  selected_urls?: string[];
  seller_mode_enabled?: boolean;
  seller_mode_names?: string[];
  seller_mode_name?: string | null;  // backward compat
  seller_filter_enabled?: boolean;
  seller_name?: string | null;      // backward compat
  exclude_competitor_enabled?: boolean;
  exclude_competitor_names?: string[];
  proxy_profile: 'interactive' | 'background_positions' | 'server';
  parallel_proxies?: boolean;
  server_proxy_ids?: number[];
  launch_payload?: Record<string, any> | null;
};

export type ParserScheduleRequest = {
  scheduled_at_msk: string;
  worksheet: string;
  mode: string;
  speed: string;
  full_fetch: boolean;
  resume: boolean;
  rerun_problematic: boolean;
  no_city_filter: boolean;
  link_format_2: boolean;
  used_only: boolean;
  only_promoted_ids: boolean;
  positions_screenshot_max_pos: number;
  periodic_end_time?: string | null;
  periodic_end_at_msk?: string | null;
  periodic_interval: number | null;
  profile_url?: string | null;
  iphone_only?: boolean;
  city_merge_mode?: boolean;
  city_merge_city?: string | null;
  city_merge_worksheets?: string[] | null;
  city_merge_market_mode?: 'account_competitors' | 'city_group_owned';
  city_template_mode?: boolean;
  city_template_city?: string | null;
  excluded_worksheet?: string | null;
  selected_urls?: string[];
  seller_mode_enabled?: boolean;
  seller_mode_names?: string[];
  seller_mode_name?: string | null;  // backward compat
  seller_filter_enabled?: boolean;
  seller_name?: string | null;      // backward compat
  exclude_competitor_enabled?: boolean;
  exclude_competitor_names?: string[];
  proxy_profile?: 'interactive' | 'background_positions' | 'server';
  parallel_proxies?: boolean;
  server_proxy_ids?: number[];
  repeat_daily: boolean;
  repeat_weekdays?: number[] | null;
};

export type ClientParserQueueItem = {
  id: string;
  client_access_key_id: number;
  account_name: string;
  worksheet_title: string | null;
  status: 'queued' | 'running' | 'success' | 'failed' | 'idle';
  queue_position: number | null;
  interactive_run_id: string | null;
  started_at: string | null;
  finished_at: string | null;
  error: string | null;
  message: string | null;
  only_promoted_ids: boolean | null;
  created_at: string;
  mode?: 'prices' | 'positions';
  server_proxy_ids?: number[];
};

export type ParserCityBatchChild = {
  city: string;
  worksheets: string[];
  status: 'queued' | 'starting' | 'running' | 'success' | 'failed' | 'cancelled';
  run_id: string | null;
  error: string | null;
  started_at: number | null;
  finished_at: number | null;
};

export type ParserCityBatchResponse = {
  batch_id: string;
  max_parallel_cities: number;
  created_at: number;
  status: 'queued' | 'running' | 'success' | 'failed' | 'stopping' | 'stopped';
  children: ParserCityBatchChild[];
  error: string | null;
  finished_at: number | null;
};

export const apiClient = {
  // Аутентификация
  login: (username: string, password: string): Promise<LoginResponse> => {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    return api.post('/api/v1/login/token', params, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }).then(res => res.data);
  },

  logout: () => {
    setAuthToken(null);
  },

  // Получить список аккаунтов
  getAccounts: (): Promise<Account[]> =>
    api.get('/api/v1/analytics/accounts').then(res => res.data),

  // Получить список моделей
  getModels: (): Promise<ModelInfo[]> =>
    api.get('/api/v1/analytics/models').then(res => res.data),

  // Данные для главной панели
  getDashboardSummary: (startDate: string, endDate: string): Promise<DashboardSummary> =>
    api.get('/api/v1/analytics/dashboard/summary', { params: { start_date_str: startDate, end_date_str: endDate } }).then(res => res.data),

  // Данные для графика расходов
  getSpendsChart: (startDate: string, endDate: string, account?: string): Promise<ChartData> =>
    api.get('/api/v1/analytics/dashboard/spends-chart', { params: { start_date_str: startDate, end_date_str: endDate, account_name: account } }).then(res => res.data),

  // Данные для графика CTR/CPL
  getCTRChart: (startDate: string, endDate: string, account?: string): Promise<ChartData> =>
    api.get('/api/v1/analytics/dashboard/ctr-chart', { params: { start_date_str: startDate, end_date_str: endDate, account_name: account } }).then(res => res.data),

  // Топ моделей
  getTopModels: (startDate: string, endDate: string, limit: number = 5, account?: string): Promise<TopModelData[]> =>
    api.get('/api/v1/analytics/dashboard/top-models', { params: { start_date_str: startDate, end_date_str: endDate, limit, account_name: account } }).then(res => res.data),

  // Распределение расходов по аккаунтам
  getSpendsByAccounts: (startDate: string, endDate: string): Promise<Array<{ account: string; spends: number }>> =>
    api.get('/api/v1/analytics/dashboard/spends-by-accounts', { params: { start_date_str: startDate, end_date_str: endDate } }).then(res => res.data),

  // Детали конкретной кампании
  getCampaignDetails: (accountName: string, startDate: string, endDate: string): Promise<CampaignMetrics> =>
    api.get(`/api/v1/analytics/campaign/${encodeURIComponent(accountName)}`, { params: { start_date_str: startDate, end_date_str: endDate } }).then(res => res.data),

  // Экспорт данных
  exportAccountData: (accountName: string, startDate: string, endDate: string): Promise<ExportData> =>
    api.get(`/api/v1/analytics/export/${encodeURIComponent(accountName)}`, { params: { start_date_str: startDate, end_date_str: endDate } }).then(res => res.data),

  // Регистрация пользователя
  register: (first_name: string, last_name: string, username: string, password: string, secret_key: string): Promise<any> =>
    api.post('/api/v1/register', {
      first_name,
      last_name,
      username,
      password,
      secret_key
    }).then(res => res.data),

  // Получить информацию о текущем пользователе
  getCurrentUser: (): Promise<any> =>
    api.get('/api/v1/me').then(res => res.data),

  // Обновить профиль пользователя
  updateProfile: (first_name?: string, last_name?: string, username?: string, telegram?: string, gender?: string, clickup_api_key?: string): Promise<any> =>
    api.patch('/api/v1/me', {
      first_name,
      last_name,
      username,
      telegram,
      gender,
      clickup_api_key
    }).then(res => res.data),

  // Изменить пароль
  updatePassword: (oldPassword: string, newPassword: string): Promise<any> =>
    api.put('/api/v1/me/password', {
      old_password: oldPassword,
      new_password: newPassword
    }).then(res => res.data),

  // Загрузить аватарку
  uploadAvatar: (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/api/v1/me/avatar', formData, {
    }).then(res => res.data);
  },


  // Удалить аватарку
  deleteAvatar: (): Promise<any> =>
    api.delete('/api/v1/me/avatar').then(res => res.data),

  // Админ: получить всех пользователей
  getAllUsers: (): Promise<any[]> =>
    api.get('/api/v1/admin/users').then(res => res.data),

  // Пользователи: получить активных пользователей для назначения в рабочих экранах
  getUsers: (params?: { is_active?: boolean }): Promise<{ users: any[] }> =>
    api.get('/api/v1/users', { params }).then(res => res.data),

  // Админ: получить активных пользователей
  getActiveUsers: (): Promise<any[]> =>
    api.get('/api/v1/admin/users/active').then(res => res.data),

  // Админ: удалить пользователя
  deleteUser: (userId: number, password?: string): Promise<any> =>
    api.delete(`/api/v1/admin/users/${userId}`, {
      data: password ? { password } : undefined,
    }).then(res => res.data),

  // Админ: закрыть все остальные сеансы пользователя
  clearOtherUserSessions: (userId: number): Promise<any> =>
    api.post(`/api/v1/admin/users/${userId}/clear-other-sessions`).then(res => res.data),

  // Админ: выдать/снять права админа
  setUserRole: (userId: number, role: 'user' | 'admin' | 'owner', password?: string): Promise<any> =>
    api.put(`/api/v1/admin/users/${userId}/role`, {
      role,
      password,
    }).then(res => res.data),

  setUserAdmin: (userId: number, is_admin: boolean, password?: string): Promise<any> =>
    api.put(`/api/v1/admin/users/${userId}/role`, {
      is_admin,
      password,
    }).then(res => res.data),

  // Админ: получить секретный ключ
  getSecretKey: (): Promise<{ secret_key: string }> =>
    api.get('/api/v1/admin/settings/secret-key').then(res => res.data),

  // Админ: обновить секретный ключ
  updateSecretKey: (new_secret_key: string): Promise<any> =>
    api.put('/api/v1/admin/settings/secret-key', {
      new_secret_key
    }).then(res => res.data),

  // Реестр (Google Sheets)
  getDolphinProxySummary: (): Promise<DolphinProxySummary> =>
    api.get('/api/v1/admin/dolphin/summary').then(res => res.data),

  syncDolphinState: (): Promise<DolphinSyncResult> =>
    api.post('/api/v1/admin/dolphin/sync').then(res => res.data),

  getDolphinProxies: (): Promise<DolphinProxyItem[]> =>
    api.get('/api/v1/admin/dolphin/proxies').then(res => res.data),

  createDolphinProxy: (payload: DolphinProxyCreateRequest): Promise<DolphinProxyItem> =>
    api.post('/api/v1/admin/dolphin/proxies', payload).then(res => res.data),

  updateDolphinProxy: (proxyId: number, payload: DolphinProxyUpdateRequest): Promise<DolphinProxyItem> =>
    api.patch(`/api/v1/admin/dolphin/proxies/${proxyId}`, payload).then(res => res.data),

  deleteDolphinProxy: (proxyId: number, detachProfiles = false): Promise<{ success: boolean }> =>
    api.delete(`/api/v1/admin/dolphin/proxies/${proxyId}`, {
      params: { detach_profiles: detachProfiles },
    }).then(res => res.data),

  checkDolphinProxy: (proxyId: number): Promise<DolphinProxyItem> =>
    api.post(`/api/v1/admin/dolphin/proxies/${proxyId}/check`).then(res => res.data),

  checkFailedDolphinProxies: (): Promise<DolphinProxyCheckBulkResult> =>
    api.post('/api/v1/admin/dolphin/proxies/check-failed').then(res => res.data),

  getDolphinProfiles: (): Promise<DolphinProfileItem[]> =>
    api.get('/api/v1/admin/dolphin/profiles').then(res => res.data),

  setDolphinProfileProxy: (
    profileId: number,
    payload: { proxy_id: number | null; note?: string | null },
  ): Promise<DolphinProfileItem> =>
    api.patch(`/api/v1/admin/dolphin/profiles/${profileId}/proxy`, payload).then(res => res.data),

  getDolphinProfileRecommendation: (profileId: number): Promise<DolphinProxyRecommendation> =>
    api.get(`/api/v1/admin/dolphin/profiles/${profileId}/recommendation`).then(res => res.data),

  bulkAssignDolphinProxy: (
    payload: { profile_ids: number[]; proxy_id: number | null; note?: string | null },
  ): Promise<{ success: boolean; changed: number; failed: number; results: Array<{ profile_id: number; success: boolean; message?: string | null }> }> =>
    api.post('/api/v1/admin/dolphin/bulk/assign-proxy', payload).then(res => res.data),

  getDolphinBatchPlan: (): Promise<{ rows: DolphinBatchPlanRow[] }> =>
    api.get('/api/v1/admin/dolphin/batch-plan').then(res => res.data),

  applyDolphinBatchPlan: (
    payload: { items: Array<{ profile_id: number; proxy_id: number }>; note?: string | null },
  ): Promise<{ success: boolean; changed: number; failed: number; results: Array<{ profile_id: number; success: boolean; message?: string | null }> }> =>
    api.post('/api/v1/admin/dolphin/batch-apply', payload).then(res => res.data),

  getDolphinOperations: (limit = 100): Promise<DolphinOperationItem[]> =>
    api.get('/api/v1/admin/dolphin/operations', { params: { limit } }).then(res => res.data),

  getRegistryAccounts: (): Promise<any[]> =>
    api.get('/api/v1/registry/accounts').then(res => res.data),

  createRegistryAccount: (payload: any): Promise<any> =>
    api.post('/api/v1/registry/accounts', payload).then(res => res.data),

  patchRegistryAccount: (rowUid: string, patch: any): Promise<any> =>
    api.patch(`/api/v1/registry/accounts/${encodeURIComponent(rowUid)}`, patch).then(res => res.data),

  clearRegistryAccount: (rowUid: string): Promise<any> =>
    api.post(`/api/v1/registry/accounts/${encodeURIComponent(rowUid)}/clear`).then(res => res.data),

  initRegistryRowUids: (): Promise<{ updated: number }> =>
    api.post('/api/v1/registry/admin/init-row-uids').then(res => res.data),

  addRegistryColumn: (headerName: string): Promise<{ added: boolean; header: string[] }> =>
    api.post('/api/v1/registry/admin/columns', { header_name: headerName }).then(res => res.data),

  getRegistryAccountPasswordStatus: (accountName: string): Promise<RegistryAccountPasswordStatus> =>
    api.get(`/api/v1/registry/accounts/${encodeURIComponent(accountName)}/password-status`).then(res => res.data),

  revealRegistryAccountPassword: (accountName: string): Promise<RegistryAccountPasswordReveal> =>
    api.get(`/api/v1/registry/accounts/${encodeURIComponent(accountName)}/password`).then(res => res.data),

  updateRegistryAccountPassword: (accountName: string, password: string | null): Promise<RegistryAccountPasswordStatus> =>
    api.put(`/api/v1/registry/accounts/${encodeURIComponent(accountName)}/password`, { password }).then(res => res.data),

  // Реестр (raw range)
  getRegistryRange: (a1Range: string = 'A1:AD120'): Promise<any> =>
    api.get('/api/v1/registry/range', { params: { a1_range: a1Range } }).then(res => res.data),

  getRegistryRangeAuto: (params?: { max_rows?: number; max_cols?: number; start_row?: number; start_col?: number }): Promise<any> =>
    api.get('/api/v1/registry/range/auto', { params }).then(res => res.data),

  patchRegistryRange: (a1Range: string, updates: Array<{ row: number; col: number; value: any }>): Promise<any> =>
    api.patch('/api/v1/registry/range', { range: a1Range, updates }).then(res => res.data),

  bulkRegistryUpdate: (
    rowUids: string[],
    updates: Array<{ header: string; value?: string | null; clear?: boolean }>,
  ): Promise<any> =>
    api.patch('/api/v1/registry/accounts/bulk', { row_uids: rowUids, updates }).then(res => res.data),

  // Парсер
  getParserPreferences: (): Promise<ParserPreferences> =>
    api.get('/api/v1/parser/preferences').then(res => res.data),

  saveParserPreferences: (payload: ParserPreferences): Promise<ParserPreferences> =>
    api.put('/api/v1/parser/preferences', payload).then(res => res.data),

  getParserStatus: (run_id?: string): Promise<any> =>
    api.get('/api/v1/parser/status', { params: { run_id } }).then(res => res.data),

  getParserStatuses: (): Promise<Record<string, any>> =>
    api.get('/api/v1/parser/statuses').then(res => {
      if (Array.isArray(res.data)) {
        return res.data.reduce((acc: any, item: any) => {
          if (item.run_id) acc[item.run_id] = item;
          return acc;
        }, {});
      }
      return res.data || {};
    }),

  startParser: (payload: {
    worksheet?: string;
    mode: 'prices' | 'positions' | 'combined' | 'periodic' | 'profile_metrics' | 'competitor_prices';
    speed?: 'fast' | 'normal';
    full_fetch?: boolean;
    resume?: boolean;
    rerun_problematic?: boolean;
    no_city_filter?: boolean;
    link_format_2?: boolean;
    used_only?: boolean;
    only_promoted_ids?: boolean;
    positions_screenshot_max_pos?: number;
    periodic_end_time?: string;
    periodic_end_at_msk?: string | null;
    periodic_interval?: number;
    profile_url?: string;
    iphone_only?: boolean;
    city_merge_mode?: boolean;
    city_merge_city?: string;
    city_merge_worksheets?: string[];
    city_merge_market_mode?: 'account_competitors' | 'city_group_owned';
    seller_mode_enabled?: boolean;
    seller_mode_names?: string[];
    seller_mode_name?: string | null;  // backward compat
    seller_filter_enabled?: boolean;
    seller_name?: string | null;      // backward compat
    exclude_competitor_enabled?: boolean;
    exclude_competitor_names?: string[];
    proxy_profile?: 'interactive' | 'background_positions' | 'server';
    parallel_proxies?: boolean;
    server_proxy_ids?: number[];
    selected_titles?: string[];
    selected_urls?: string[];
  }): Promise<any> => api.post('/api/v1/parser/start', payload).then(res => res.data),

  startParserCityBatch: (payload: {
    worksheet?: string;
    mode: 'prices' | 'positions' | 'combined';
    speed?: 'fast' | 'normal';
    full_fetch?: boolean;
    resume?: boolean;
    rerun_problematic?: boolean;
    no_city_filter?: boolean;
    link_format_2?: boolean;
    used_only?: boolean;
    only_promoted_ids?: boolean;
    positions_screenshot_max_pos?: number;
    city_merge_mode?: boolean;
    city_merge_cities: string[];
    city_merge_city_worksheets: Record<string, string[]>;
    city_merge_market_mode?: 'account_competitors' | 'city_group_owned';
    max_parallel_cities: number;
    seller_mode_enabled?: boolean;
    seller_mode_names?: string[];
    seller_mode_name?: string | null;  // backward compat
    seller_filter_enabled?: boolean;
    seller_name?: string | null;      // backward compat
    exclude_competitor_enabled?: boolean;
    exclude_competitor_names?: string[];
    proxy_profile?: 'interactive' | 'background_positions' | 'server';
    parallel_proxies?: boolean;
    server_proxy_ids?: number[];
  }): Promise<ParserCityBatchResponse> =>
    api.post('/api/v1/parser/start-batch', payload).then(res => res.data),

  getParserCityAccounts: (): Promise<{
    cities: string[];
    accounts_by_city: Record<string, Array<{ name: string; emoji: string | null }>>;
  }> => api.get('/api/v1/parser/city-accounts').then(res => res.data),

  stopParser: (run_id?: string | null): Promise<any> =>
    api.post('/api/v1/parser/stop', null, { params: run_id ? { run_id } : undefined }).then(res => res.data),

  assignParserCaptcha: (opts?: { force?: boolean }): Promise<any> =>
    api.post('/api/v1/parser/captcha/assign', null, { params: { force: !!opts?.force } }).then(res => res.data),

  getParserCaptchaAccess: (): Promise<ParserCaptchaAccess> =>
    api.get('/api/v1/parser/captcha-access').then(res => res.data),

  downloadParserRdpFile: (): Promise<Blob> =>
    api.get('/api/v1/parser/rdp-file', { responseType: 'blob' }).then(res => res.data),

  getParserWorksheets: (): Promise<{ sheet_id: string; worksheets: string[] }> =>
    api.get('/api/v1/parser/worksheets').then(res => res.data),

  getParserWorksheetItems: (worksheet: string): Promise<{
    worksheet: string;
    items: Array<{ row_number: number; title: string; url: string; my_ids_count?: number }>;
  }> =>
    api.get('/api/v1/parser/worksheet-items', { params: { worksheet } }).then(res => res.data),

  getParserRuns: (limit = 50): Promise<{ runs: any[] }> =>
    api.get('/api/v1/parser/runs', { params: { limit } }).then(res => res.data),

  getClientParserQueue: (): Promise<{ items: ClientParserQueueItem[] }> =>
    api.get('/api/v1/parser/client-queue').then(res => res.data),

  // Internal parser queue
  addToParserQueue: (body: {
    is_city_batch: boolean;
    payload: Record<string, any>;
    city_batch_payload?: Record<string, any>;
    label: string;
  }): Promise<{ id: string; queued_at: string; label: string }> =>
    api.post('/api/v1/parser/queue', body).then(res => res.data),

  addBulkToParserQueue: (items: Array<{
    is_city_batch: boolean;
    payload: Record<string, any>;
    city_batch_payload?: Record<string, any>;
    label: string;
  }>): Promise<{ items: Array<{ id: string; queued_at: string; label: string }> }> =>
    api.post('/api/v1/parser/queue/bulk', { items }).then(res => res.data),

  getParserQueue: (): Promise<Array<{
    id: string;
    queued_at: string;
    is_city_batch: boolean;
    payload: Record<string, any>;
    city_batch_payload?: Record<string, any>;
    label: string;
  }>> => api.get('/api/v1/parser/queue').then(res => res.data),

  removeFromParserQueue: (id: string): Promise<{ ok: boolean }> =>
    api.delete(`/api/v1/parser/queue/${id}`).then(res => res.data),

  reorderParserQueue: (id: string, new_position: number): Promise<{ ok: boolean }> =>
    api.patch('/api/v1/parser/queue/reorder', { id, new_position }).then(res => res.data),

  dispatchParserQueue: (): Promise<{ dispatched: boolean; id?: string; error?: string }> =>
    api.post('/api/v1/parser/queue/dispatch').then(res => res.data),

  getScreenshotsZipStatus: (run_id?: string): Promise<{ ready: boolean }> =>
    api.get('/api/v1/parser/screenshots/zip/status', { params: run_id ? { run_id } : undefined }).then(res => res.data),

  getParserSummary: (mode: 'prices' | 'positions' | 'periodic' | 'profile_metrics' | 'competitor_prices', run_id?: string): Promise<any> =>
    api.get('/api/v1/parser/summary', { params: { mode, run_id } }).then(res => res.data),

  getParserResults: (
    url?: string,
    run_id?: string,
  ): Promise<{ path: string; mtime: number | null; size: number | null; rows: any[] }> =>
    api.get('/api/v1/parser/results', { params: { url, run_id } }).then(res => res.data),

  getParserTop10: (
    url?: string,
    run_id?: string,
  ): Promise<{ path: string; mtime: number | null; size: number | null; rows: any[] }> =>
    api.get('/api/v1/parser/top10', { params: { url, run_id } }).then(res => res.data),

  getParserLogTail: (
    lines = 200,
    run_id?: string,
  ): Promise<{ path: string; mtime: number | null; size: number | null; lines: string[] }> =>
    api.get('/api/v1/parser/log-tail', { params: { lines, run_id } }).then(res => res.data),

  downloadParserArtifact: (name: 'log' | 'results' | 'top10' | 'summary' | 'positions', run_id?: string): Promise<Blob> =>
    api.get('/api/v1/parser/download', { params: { name, run_id }, responseType: 'blob' }).then(res => res.data),

  getParserScreenshotUrl: (filename: string, run_id?: string): string => {
    const params = new URLSearchParams({ filename });
    if (run_id) params.set('run_id', run_id);
    return `/api/v1/parser/screenshot?${params.toString()}`;
  },

  getParserScreenshotsZipToken: (run_id?: string): Promise<{ token: string; download_url: string; filename: string; expires_in: number }> =>
    api.get('/api/v1/parser/screenshots/zip/token', { params: run_id ? { run_id } : undefined }).then(res => res.data),

  // Cookie Pool
  getCookiePoolStatus: (): Promise<any> =>
    api.get('/api/v1/parser/cookie-pool-status').then(res => res.data),

  startCookiePool: (): Promise<any> =>
    api.post('/api/v1/parser/cookie-pool/start').then(res => res.data),

  stopCookiePool: (): Promise<any> =>
    api.post('/api/v1/parser/cookie-pool/stop').then(res => res.data),

  getCookieHistory: (run_id?: string | null): Promise<{ events: CookieEvent[] }> =>
    api.get('/api/v1/parser/cookie-history', { params: run_id ? { run_id } : {} }).then(res => res.data),

  // Планировщик парсера
  getScheduledJobs: (): Promise<ScheduledJobResponse[]> =>
    api.get('/api/v1/parser/schedule').then(res => res.data),

  createScheduledJob: (payload: ParserScheduleRequest): Promise<ScheduledJobResponse> =>
    api.post('/api/v1/parser/schedule', payload).then(res => res.data),

  createScheduledJobsBatch: (payloads: ParserScheduleRequest[]): Promise<ScheduledJobResponse[]> =>
    api.post('/api/v1/parser/schedule/batch', { jobs: payloads }).then(res => res.data),

  editScheduledJob: (jobId: string, payload: ParserScheduleRequest): Promise<ScheduledJobResponse> =>
    api.put(`/api/v1/parser/schedule/${jobId}`, payload).then(res => res.data),

  cancelScheduledJob: (jobId: string): Promise<any> =>
    api.delete(`/api/v1/parser/schedule/${jobId}`).then(res => res.data),

  runScheduledJobNow: (jobId: string): Promise<any> =>
    api.post(`/api/v1/parser/schedule/${jobId}/run-now`).then(res => res.data),

  getPhotoFlowWatermarks: (): Promise<{ watermarks: string[] }> =>
    api.get('/api/v1/photoflow/watermarks').then(res => res.data),

  getPhotoFlowWatermarkFile: (name: string): Promise<Blob> =>
    api.get('/api/v1/photoflow/watermarks/file', { params: { name }, responseType: 'blob' }).then(res => res.data),

  previewPhotoFlow: (formData: FormData): Promise<Blob> =>
    api.post('/api/v1/photoflow/preview', formData, {
      responseType: 'blob',
    }).then(res => res.data),

  getPhotoFlowExif: (formData: FormData): Promise<{ exif: Record<string, any> }> =>
    api.post('/api/v1/photoflow/exif', formData, {
    }).then(res => res.data),

  processPhotoFlow: (formData: FormData): Promise<Blob> =>
    api.post('/api/v1/photoflow/process', formData, {
      responseType: 'blob',
    }).then(res => res.data),

  // Объявления (публичные)
  getActiveAnnouncement: (): Promise<{ announcements: any[] }> =>
    api.get('/api/v1/announcements/active').then(res => res.data),

  // Объявления (только админ)
  getAdminAnnouncements: (): Promise<{ announcements: any[] }> =>
    api.get('/api/v1/admin/announcements').then(res => res.data),

  createAnnouncement: (data: { title: string; message: string; type: string; is_active: boolean }): Promise<any> =>
    api.post('/api/v1/admin/announcements', data).then(res => res.data),

  updateAnnouncement: (id: string, data: Partial<{ title: string; message: string; type: string; is_active: boolean }>): Promise<any> =>
    api.put(`/api/v1/admin/announcements/${id}`, data).then(res => res.data),

  deleteAnnouncement: (id: string): Promise<any> =>
    api.delete(`/api/v1/admin/announcements/${id}`).then(res => res.data),

  // Настройки бота
  getBotAccounts: (): Promise<BotAccount[]> =>
    api.get('/api/v1/bot/accounts').then(res => res.data),

  patchBotNightMode: (accountName: string, patch: Partial<NightModeSettings>): Promise<NightModeSettings> =>
    api.patch(`/api/v1/bot/accounts/${encodeURIComponent(accountName)}/night-mode`, patch).then(res => res.data),

  patchBotBalanceThresholds: (accountName: string, patch: Partial<BotBalanceThresholds>): Promise<BotBalanceThresholds> =>
    api.patch(`/api/v1/bot/accounts/${encodeURIComponent(accountName)}/balance-thresholds`, patch).then(res => res.data),

  pauseBotAccount: (accountName: string): Promise<BotPauseResult> =>
    api.post(`/api/v1/bot/accounts/${encodeURIComponent(accountName)}/pause`).then(res => res.data),

  unpauseBotAccount: (accountName: string): Promise<BotPauseResult> =>
    api.post(`/api/v1/bot/accounts/${encodeURIComponent(accountName)}/unpause`).then(res => res.data),

  getBotExclusions: (accountName: string): Promise<{ account_name: string; item_ids: number[] }> =>
    api.get(`/api/v1/bot/accounts/${encodeURIComponent(accountName)}/exclusions`).then(res => res.data),

  setBotExclusions: (accountName: string, itemIds: number[]): Promise<{ account_name: string; item_ids: number[] }> =>
    api.put(`/api/v1/bot/accounts/${encodeURIComponent(accountName)}/exclusions`, { item_ids: itemIds }).then(res => res.data),

  getBotAccountItems: (accountName: string): Promise<{ account_name: string; items: BotItem[] }> =>
    api.get(`/api/v1/bot/accounts/${encodeURIComponent(accountName)}/items`).then(res => res.data),

  addBotAccount: (data: AddBotAccountRequest): Promise<BotAccount> =>
    api.post('/api/v1/bot/accounts', data).then(res => res.data),

  syncBotAccountsFromRegistry: (): Promise<{
    added: string[];
    updated: string[];
    paused: string[];
    unpaused: string[];
    bot_enabled: string[];
    bot_disabled: string[];
    paused_manual: string[];
    already_synced: string[];
    skipped_no_credentials: string[];
    already_exist: string[];
    deduped: string[];
  }> =>
    api.post('/api/v1/bot/accounts/sync-registry').then(res => res.data),

  listContractTemplates: (): Promise<ContractTemplateSummary[]> =>
    api.get('/api/v1/contracts/templates').then(res => res.data),

  uploadContractTemplate: (payload: {
    file: File;
    name: string;
    placeholderPrefix?: string;
    placeholderSyntax?: 'double_curly' | 'prefix' | 'pdf_fields';
    nextNumber?: number;
    numberPlaceholderKey?: string;
    clientSlugKey?: string;
  }): Promise<ContractTemplateDetail> => {
    const formData = new FormData();
    formData.append('file', payload.file);
    formData.append('name', payload.name);
    formData.append('placeholder_prefix', payload.placeholderPrefix ?? '');
    formData.append('placeholder_syntax', payload.placeholderSyntax ?? 'double_curly');
    formData.append('next_number', String(payload.nextNumber ?? 1));
    formData.append('number_placeholder_key', payload.numberPlaceholderKey ?? 'contract_number');
    formData.append('client_slug_key', payload.clientSlugKey ?? 'customer_name');
    return api.post('/api/v1/contracts/templates', formData).then(res => res.data);
  },

  getContractTemplate: (templateId: number): Promise<ContractTemplateDetail> =>
    api.get(`/api/v1/contracts/templates/${templateId}`).then(res => res.data),

  downloadContractTemplateFile: (templateId: number): Promise<GenerateContractResponse> =>
    api.get(`/api/v1/contracts/templates/${templateId}/file`, {
      responseType: 'blob',
    }).then((res) => ({
      blob: res.data,
      filename: parseFilenameFromDisposition(
        res.headers['content-disposition'],
        res.headers['content-type'],
      ),
    })),

  replaceContractTemplateFile: (templateId: number, file: File): Promise<ContractTemplateDetail> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/api/v1/contracts/templates/${templateId}/file`, formData).then(res => res.data);
  },

  updateContractTemplate: (
    templateId: number,
    payload: UpdateContractTemplateRequest,
  ): Promise<ContractTemplateDetail> =>
    api.patch(`/api/v1/contracts/templates/${templateId}`, payload).then(res => res.data),

  deleteContractTemplate: (templateId: number): Promise<void> =>
    api.delete(`/api/v1/contracts/templates/${templateId}`).then(() => undefined),

  generateContractTemplate: (
    templateId: number,
    payload: GenerateContractRequest,
  ): Promise<GenerateContractResponse> =>
    api.post(`/api/v1/contracts/templates/${templateId}/generate`, payload, {
      responseType: 'blob',
    }).then((res) => ({
      blob: res.data,
      filename: parseFilenameFromDisposition(
        res.headers['content-disposition'],
        res.headers['content-type'],
      ),
    })),

  listContractGenerations: (templateId: number): Promise<ContractGenerationSummary[]> =>
    api.get(`/api/v1/contracts/templates/${templateId}/generations`).then(res => res.data),

  getContractGeneration: (generationId: number): Promise<ContractGenerationDetail> =>
    api.get(`/api/v1/contracts/generations/${generationId}`).then(res => res.data),

  regenerateContractGeneration: (
    generationId: number,
    payload?: { output_format?: 'pdf' | 'docx' },
  ): Promise<GenerateContractResponse> =>
    api.post(`/api/v1/contracts/generations/${generationId}/regenerate`, payload ?? {}, {
      responseType: 'blob',
    }).then((res) => ({
      blob: res.data,
      filename: parseFilenameFromDisposition(
        res.headers['content-disposition'],
        res.headers['content-type'],
      ),
    })),

  // Client Keys API
  createClientKey: (data: CreateClientKeyRequest): Promise<ClientKey> =>
    api.post('/api/v1/client-keys/', data).then(res => res.data),

  listClientKeys: (): Promise<ClientKey[]> =>
    api.get('/api/v1/client-keys/').then(res => res.data),

  getClientKey: (keyId: number): Promise<ClientKeyDetail> =>
    api.get(`/api/v1/client-keys/${keyId}`).then(res => res.data),

  updateClientKey: (keyId: number, data: UpdateClientKeyRequest): Promise<ClientKey> =>
    api.patch(`/api/v1/client-keys/${keyId}`, data).then(res => res.data),

  deleteClientKey: (keyId: number): Promise<{ message: string }> =>
    api.delete(`/api/v1/client-keys/${keyId}`).then(res => res.data),

  regenerateClientKey: (keyId: number): Promise<ClientKey> =>
    api.post(`/api/v1/client-keys/${keyId}/regenerate`, {}).then(res => res.data),

  getKeyChats: (keyId: number): Promise<ClientChat[]> =>
    api.get(`/api/v1/client-keys/${keyId}/chats`).then(res => res.data),

  getClientKeysStats: (): Promise<ClientKeysStats> =>
    api.get('/api/v1/client-keys/stats/overview').then(res => res.data),

  // Server Proxies (profile_metrics)
  getServerProxies: (): Promise<ServerProxyItem[]> =>
    api.get('/api/v1/parser/server-proxies').then(res => res.data),

  createServerProxy: (payload: ServerProxyCreateRequest): Promise<ServerProxyItem> =>
    api.post('/api/v1/parser/server-proxies', payload).then(res => res.data),

  createServerProxiesBulk: (payload: { proxies: ServerProxyCreateRequest[] }): Promise<ServerProxyBulkCreateResult> =>
    api.post('/api/v1/parser/server-proxies/bulk', payload).then(res => res.data),

  updateServerProxy: (proxyId: number, payload: ServerProxyUpdateRequest): Promise<ServerProxyItem> =>
    api.patch(`/api/v1/parser/server-proxies/${proxyId}`, payload).then(res => res.data),

  deleteServerProxy: (proxyId: number): Promise<{ ok: boolean }> =>
    api.delete(`/api/v1/parser/server-proxies/${proxyId}`).then(res => res.data),

  checkServerProxy: (proxyId: number): Promise<ServerProxyItem> =>
    api.post(`/api/v1/parser/server-proxies/${proxyId}/check`).then(res => res.data),

  activateServerProxy: (proxyId: number): Promise<ServerProxyItem> =>
    api.post(`/api/v1/parser/server-proxies/${proxyId}/activate`).then(res => res.data),

  moveServerProxy: (proxyId: number, direction: 'up' | 'down'): Promise<ServerProxyItem> =>
    api.post(`/api/v1/parser/server-proxies/${proxyId}/move`, null, { params: { direction } }).then(res => res.data),

  resetServerProxy: (proxyId: number): Promise<ServerProxyItem> =>
    api.post(`/api/v1/parser/server-proxies/${proxyId}/reset`).then(res => res.data),

  resetServerProxyFailures: (): Promise<{ ok: boolean; updated: number }> =>
    api.post('/api/v1/parser/server-proxies/reset-failures').then(res => res.data),

  getServerProxyProviderStatus: (): Promise<ServerProxyProviderStatus> =>
    api.get('/api/v1/parser/server-proxies/provider/status').then(res => res.data),

  syncServerProxyProvider: (): Promise<ServerProxyProviderSyncResult> =>
    api.post('/api/v1/parser/server-proxies/provider/sync').then(res => res.data),

  getServerProxyProviderGeo: (proxyId?: number): Promise<any> =>
    api.get('/api/v1/parser/server-proxies/provider/geo', { params: proxyId ? { proxy_id: proxyId } : undefined }).then(res => res.data),

  previewServerProxyPurchase: (payload: ServerProxyProviderPurchaseRequest): Promise<any> =>
    api.post('/api/v1/parser/server-proxies/provider/purchase/preview', payload).then(res => res.data),

  confirmServerProxyPurchase: (payload: ServerProxyProviderPurchaseRequest): Promise<any> =>
    api.post('/api/v1/parser/server-proxies/provider/purchase/confirm', payload).then(res => res.data),

  changeServerProxyEquipmentProvider: (proxyId: number, payload: ServerProxyProviderChangeEquipmentRequest): Promise<any> =>
    api.post(`/api/v1/parser/server-proxies/${proxyId}/provider/change-equipment`, payload).then(res => res.data),

  rebootServerProxyProvider: (proxyId: number): Promise<any> =>
    api.post(`/api/v1/parser/server-proxies/${proxyId}/provider/reboot`).then(res => res.data),

  updateServerProxyProviderSettings: (proxyId: number, payload: ServerProxyProviderSettingsRequest): Promise<any> =>
    api.patch(`/api/v1/parser/server-proxies/${proxyId}/provider/settings`, payload).then(res => res.data),

  // Calls (call bookings)
  getCalls: (params?: Record<string, string>): Promise<{ calls: any[] }> =>
    api.get('/api/v1/calls', { params }).then(res => res.data),

  getCallProjectManagers: (): Promise<{ project_manager_user_ids: number[] }> =>
    api.get('/api/v1/calls/project-managers').then(res => res.data),

  updateCallProjectManagers: (projectManagerUserIds: number[]): Promise<{ project_manager_user_ids: number[] }> =>
    api.put('/api/v1/calls/project-managers', { project_manager_user_ids: projectManagerUserIds }).then(res => res.data),

  createCall: (payload: any): Promise<any> =>
    api.post('/api/v1/calls', payload).then(res => res.data),

  patchCall: (id: number, payload: any): Promise<any> =>
    api.patch(`/api/v1/calls/${id}`, payload).then(res => res.data),

  deleteCall: (id: number): Promise<any> =>
    api.delete(`/api/v1/calls/${id}`).then(res => res.data),

  // PM schedule (project manager's calendar)
  getMyPMSettings: (): Promise<any> =>
    api.get('/api/v1/calls/pm-schedule/me/settings').then(res => res.data),

  updateMyPMSettings: (payload: {
    slot_minutes?: number;
    work_start_hour?: number;
    work_end_hour?: number;
    weekdays?: number[];
    is_active?: boolean;
  }): Promise<any> =>
    api.put('/api/v1/calls/pm-schedule/me/settings', payload).then(res => res.data),

  getPMSettings: (pmUserId: number): Promise<any> =>
    api.get(`/api/v1/calls/pm-schedule/users/${pmUserId}/settings`).then(res => res.data),

  getPMAvailability: (pmUserId: number, from: string, to: string): Promise<any> =>
    api.get(`/api/v1/calls/pm-schedule/users/${pmUserId}/availability`, {
      params: { from, to },
    }).then(res => res.data),

  listMyDayBlocks: (params?: { from?: string; to?: string }): Promise<{ blocks: any[] }> =>
    api.get('/api/v1/calls/pm-schedule/me/day-blocks', { params }).then(res => res.data),

  createMyDayBlock: (payload: {
    block_date: string;
    start_hour: number;
    end_hour: number;
    reason?: string;
  }): Promise<any> =>
    api.post('/api/v1/calls/pm-schedule/me/day-blocks', payload).then(res => res.data),

  deleteMyDayBlock: (blockId: number): Promise<any> =>
    api.delete(`/api/v1/calls/pm-schedule/me/day-blocks/${blockId}`).then(res => res.data),

};

export default apiClient;
